import React, { useEffect, useRef } from 'react';

/**
 * JitsiFrame — lightweight wrapper around the Jitsi Meet External API.
 *
 * Jitsi Meet's public server (meet.jit.si) is FREE, unlimited, needs
 * no API key, and supports up to ~75 participants per room. We lazy
 * load the external API script once per page, then mount the call in
 * the provided container. Clean teardown on unmount / room change.
 */
const JITSI_SCRIPT_URL = 'https://meet.jit.si/external_api.js';
let scriptPromise = null;
const loadScript = () => {
  if (scriptPromise) return scriptPromise;
  scriptPromise = new Promise((resolve, reject) => {
    if (window.JitsiMeetExternalAPI) { resolve(); return; }
    const s = document.createElement('script');
    s.src = JITSI_SCRIPT_URL;
    s.async = true;
    s.onload = () => resolve();
    s.onerror = reject;
    document.head.appendChild(s);
  });
  return scriptPromise;
};

export default function JitsiFrame({ roomSlug, displayName, subject, onLeave }) {
  const containerRef = useRef(null);
  const apiRef = useRef(null);

  useEffect(() => {
    let cancelled = false;
    loadScript().then(() => {
      if (cancelled || !containerRef.current || !roomSlug) return;
      apiRef.current = new window.JitsiMeetExternalAPI('meet.jit.si', {
        roomName: roomSlug,
        parentNode: containerRef.current,
        width: '100%',
        height: '100%',
        userInfo: { displayName: displayName || 'K9os Guest' },
        configOverwrite: {
          startWithAudioMuted: false,
          startWithVideoMuted: false,
          prejoinPageEnabled: true,
          subject: subject || 'K9os Call',
          disableThirdPartyRequests: true,
        },
        interfaceConfigOverwrite: {
          MOBILE_APP_PROMO: false,
          SHOW_JITSI_WATERMARK: false,
          SHOW_WATERMARK_FOR_GUESTS: false,
          DEFAULT_BACKGROUND: '#0B1220',
          DEFAULT_REMOTE_DISPLAY_NAME: 'Guest',
        },
      });
      apiRef.current.addEventListener('readyToClose', () => onLeave && onLeave());
      apiRef.current.addEventListener('videoConferenceLeft', () => onLeave && onLeave());
    }).catch(console.error);
    return () => { cancelled = true; try { apiRef.current?.dispose(); } catch (_e) {} };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [roomSlug]);

  return (
    <div
      ref={containerRef}
      className='w-full h-full min-h-[60vh] bg-black rounded-xl overflow-hidden'
      data-testid='jitsi-frame'
    />
  );
}
